/*
 * Copyright Mimic Networks, Inc. 2024.
 */

import { CloseOutlined } from '@ant-design/icons';

import { ConfigRevision } from '@/client';
import { MimicSyntaxHighlighter } from '@/components/MimicSyntaxHighlighter';
import { RelativeTime } from '@/components/RelativeTime';
import { Avatar } from '@/primitives/Avatar';
import { Button } from '@/primitives/Button';
import { Card } from '@/primitives/Card';
import { Col } from '@/primitives/Col';
import { Flex } from '@/primitives/Flex';
import { Text } from '@/primitives/Text';
import { VerticalDivider } from '@/primitives/VerticalDivider';
import './ConfigureNodesJobWizardSelectConfigRevisionCode.css';

export type ConfigureNodesJobWizardSelectConfigRevisionCodeProps = {
  configRevision: ConfigRevision | undefined;
  setShowConfigCode: (showConfigCode: boolean) => void;
};

export function ConfigureNodesJobWizardSelectConfigRevisionCode({
  configRevision,
  setShowConfigCode,
}: ConfigureNodesJobWizardSelectConfigRevisionCodeProps) {
  if (!configRevision) {
    return null;
  }

  return (
    <Col xs={24} xl={12}>
      <Card padding="sm" style={{ width: '100%' }} gap="xs">
        <Flex align="center" justify="space-between" full>
          <Text type="default" size="md" mono>
            {configRevision.filename}
          </Text>
          <Button dataTestId="close-job-wizard" type="text" onClick={() => setShowConfigCode(false)}>
            <CloseOutlined />
          </Button>
        </Flex>
        <Text
          type="default"
          size="lg"
          data-testid="config-revision-description"
          strong
        >{`Revision #${configRevision.revisionNumber}: ${configRevision.description}`}</Text>
        <div className="cnjw-config-revision-details-container">
          <div className="cnjw-config-revision-details-created-by">
            <Avatar text={configRevision.createdBy.displayName[0]} size="xs" dataTestId="display-name" />
            <Text type="subtle" size="sm">
              {configRevision.createdBy.displayName},
            </Text>
          </div>
          <div className="cnjw-config-revision-details-created-date">
            <Text type="subtle" size="sm">
              <RelativeTime date={configRevision!.createdAt} />
            </Text>
            <VerticalDivider />
          </div>
          <div className="cnjw-config-revision-details-id">
            <Text type="subtle" size="sm" mono copyable>
              {configRevision.id}
            </Text>
          </div>
        </div>
      </Card>
      <MimicSyntaxHighlighter source={configRevision.source} />
    </Col>
  );
}
